import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, OnDestroy, OnInit } from '@angular/core';
import { ChartService } from '../chart.service';
import { PieChart } from '../model/pie-chart';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ChartModule } from 'primeng/chart';
import { CommonModule } from '@angular/common';
import { generate, Observable, Subscription, tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AppService } from 'app/app.service';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { Router } from '@angular/router';
import { overrides } from 'chart.js/dist/core/core.defaults';
import { Chart } from 'chart.js';

@Component({
  selector: 'app-pie-chart',
  standalone: true,
  imports: [
    CommonModule,
    ChartModule,
    BreadcrumbComponent
  ],
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PieChartComponent implements OnInit, DoCheck, OnDestroy{

  documentStyle = getComputedStyle(document.documentElement);

  chartData: any;
  chartTitle: string = '';
  timeByAircraft?: PieChart;
  timeBySeat?: PieChart;
  emptyChart?: PieChart;

  options: any;
  lightModeOptions: any;
  darkModeOptions: any;
  previousTheme: string | undefined;

  theme$?: string;
  sub?: Subscription;

  constructor(
    public appService: AppService,
    private messageSerive: MessageService,
    private chartService: ChartService,
    private errorLogService: ErrorLogService,
    private cdr: ChangeDetectorRef,
    public router: Router
  ) { }

  chartResponseData$?: Observable<PieChart>;

  ngOnInit(): void {

    this.sub = this.appService.theme$.subscribe((theme) => {
      this.theme$ = theme;
      this.cdr.markForCheck();
    })

    const chartSelected = JSON.parse(sessionStorage.getItem('chartId') || '');
    if (chartSelected == '1') {
      this.chartTitle = 'Time by Seat';
      this.chartResponseData$ = this.chartService.getBySeatPieChart().pipe(
        tap((chartResponse) => {
          if (!chartResponse) {
            this.errorLogService.logError(`Error or no response from chart service`);
            this.messageSerive.add({ severity: 'error', summary: 'Error', detail: 'No chart data found' });
            this.loadEmptyChart();
            return;
          }
          this.timeBySeat = chartResponse;
          this.loadOptionsSeat();
          this.formatTimebySeatChartData();
        })
      );
    }
    else {
      this.chartTitle = 'Time by Aircraft';
      this.chartResponseData$ = this.chartService.getByAircraftPieChart().pipe(
        tap((chartResponse) => {
          if (!chartResponse) {
            this.errorLogService.logError(`Error or no response from chart service`);
            this.messageSerive.add({ severity: 'error', summary: 'Error', detail: 'No chart data found' });
            this.loadEmptyChart();
            return;
          }
          this.timeByAircraft = chartResponse;
          this.loadOptionsAircraft();
          this.formatTimebyAircraftChartData();
        })
      );
    }

    
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  ngDoCheck(): void {
    const currentTheme = this.theme$;
    if (this.previousTheme !== currentTheme) {
      this.previousTheme = currentTheme;
      this.options = this.toggleThemeOptions();

      if (this.timeByAircraft || this.timeBySeat) {
        if (this.timeByAircraft) {
          this.chartTitle = 'Time by Aircraft';
          this.formatTimebyAircraftChartData();
        }
        else {
          this.chartTitle = 'Time by Seat';
          this.formatTimebySeatChartData();
        }
      }
      else {
        this.loadEmptyChart();
      }
    }
  }


  toggleThemeOptions(): any {
    if (this.theme$ === 'light') {
      return this.lightModeOptions;
    } else {
      return this.darkModeOptions;
    }
  }

  formatTimebySeatChartData() {
    if (this.theme$ === 'light') {
      this.chartData = {
        labels: this.timeBySeat?.labels,
        datasets: [
          {
            data: this.timeBySeat?.data,
            backgroundColor: this.chartService.lightColorBackground(),
            hoverBackgroundColor: this.chartService.hoverColorBackground()
          }
        ]
      };

    }
    else {
      this.chartData = {
        labels: this.timeBySeat?.labels,
        datasets: [
          {
            data: this.timeBySeat?.data,
            backgroundColor: this.chartService.darkColorBackground(),
            hoverBackgroundColor: this.chartService.hoverColorBackground()
          }
        ]
      };
    }
  }

  formatTimebyAircraftChartData() {
    // this.chartTitle = 'Time by Aircraft';
    if (this.theme$ === 'light') {
      this.chartData = {
        labels: this.timeByAircraft?.labels,
        datasets: [
          {
            data: this.timeByAircraft?.data,
            backgroundColor: this.chartService.lightColorBackground(),
            hoverBackgroundColor: this.chartService.hoverColorBackground()
          }
        ]
      };
    }
    else {
      this.chartData = {
        labels: this.timeByAircraft?.labels,
        datasets: [
          {
            data: this.timeByAircraft?.data,
            backgroundColor: this.chartService.darkColorBackground(),
            hoverBackgroundColor: this.chartService.hoverColorBackground()
          }
        ]
      };
    }
  }

  loadEmptyChart(): void {
    if (this.theme$ === 'light') {
      this.chartData = {
        labels: ['N/A'],
        datasets: [
          {
            data: [100],
            backgroundColor: '#1c80cf50',
            hoverBackgroundColor: '#75BEF850'
          }
        ]
      };
    }
    else {
      this.chartData = {
        labels: ['N/A'],
        datasets: [
          {
            data: [100],
            backgroundColor: '#1c80cf50',
            hoverBackgroundColor: '#75BEF850'
          }
        ]
      };
    }
  }

  loadOptionsAircraft(): void {
    this.lightModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      events: ['click'],
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            color: '#35454c'
          }
        }
      },
      onClick: (event: any, item: any) => {
        sessionStorage.setItem('to', JSON.stringify('analyzeChart-variants'))
        this.router.navigate(['analyze/pie-chart/variants', item[0].index]);
      }
    };

    this.darkModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      events: ['click'],
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            color: '#e7e7e7'
          }
        }
      },
      onClick: (event: any, item: any) => {
        sessionStorage.setItem('to', JSON.stringify('analyzeChart-variants'))
        this.router.navigate(['analyze/pie-chart/variants', item[0].index]);
      }
    };

    this.options = this.lightModeOptions;
  }

  loadOptionsSeat(): void {
    this.lightModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      events: ['click'],
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            color: '#35454c'
          }
        }
      }
    };

    this.darkModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            color: '#e7e7e7'
          }
        }
      }
    };

    this.options = this.lightModeOptions;
  }


  seatName(seat: string): string {
    switch (seat) {
      case 'CA':
        return 'Captain (CA)';
      case 'FO':
        return 'First Officer (FO)';
      case 'FC':
        return 'First Officer (FC)';
      case 'FB':
        return 'First Officer (FB)';
      case 'XA':
        return 'Check Airman (XA)';
      case 'XO':
        return 'Check Airman (XO)';
      case 'RC':
        return 'Captain (RC)';
      case 'FE':
        return 'Flight Engineer (FE)';
      default:
        return 'Unknown';
    }
  }

}


